import { simpleDeepCopy } from 'shared/utils/utils';

export interface MigrationRule<T extends VersionedObject = VersionedObject> {
  isSuitable: (object: T, isMigratingUp: boolean) => boolean;
  migrateUp?: (object: T) => T;
  migrateDown?: (object: T) => T;
}

export interface Conversion<T extends VersionedObject = VersionedObject> {
  version: string;
  isSuitableVersion?: (toVersion: string, version: string) => boolean;
  isSuitableObject?: (object: T, version: string) => boolean;
  shouldMigrate?: MigrationPredicate<T>;
  rules: MigrationRule<T>[];
}

export interface VersionedObject {
  version?: string;
  [key: string]: unknown;
}

export interface MigrationResult<T extends VersionedObject> {
  objects: T[];
  isAnyMigrated: boolean;
}

export type MigrationPredicate<T extends VersionedObject> = (_object: T, _isMigratingUp: boolean) => boolean;

export function migrateToTheLatest<T extends VersionedObject>(
  objects: T[],
  conversions: Conversion<T>[],
  shouldMigrate?: MigrationPredicate<T>,
): MigrationResult<T> {
  return migrateByIndex(objects, conversions, conversions.length - 1, shouldMigrate);
}

// toVersion null means "unversioned" — migrates down past all conversions
export function migrate<T extends VersionedObject>(
  objects: T[],
  conversions: Conversion<T>[],
  toVersion: string | null = null,
  shouldMigrate?: MigrationPredicate<T>,
): MigrationResult<T> {
  const toIndex = toVersion === null
    ? -1
    : conversions.findIndex(({ version, isSuitableVersion }) =>
        version === toVersion || isSuitableVersion?.(toVersion, version)
      );
  return migrateByIndex(objects, conversions, toIndex, shouldMigrate);
}

function migrateByIndex<T extends VersionedObject>(
  objects: T[],
  conversions: Conversion<T>[],
  toIndex: number,
  shouldMigrate?: MigrationPredicate<T>,
): MigrationResult<T> {
  let isAnyMigrated = false;

  const migrated = objects.map(object => {
    const fromIndex = conversions.findIndex(({ version, isSuitableObject }) =>
      version === object.version || isSuitableObject?.(object, version)
    );

    if (fromIndex === -1 || fromIndex === toIndex) {
      return object;
    }

    const isMigratingUp = toIndex > fromIndex;
    if (shouldMigrate && !shouldMigrate(object, isMigratingUp)) {
      return object;
    }

    const { object: out, isMigrated } = migrateObject(object, conversions, fromIndex, toIndex);
    if (isMigrated) isAnyMigrated = true;
    return out;
  });

  return { objects: migrated, isAnyMigrated };
}

function migrateObject<T extends VersionedObject>(
  object: T,
  conversions: Conversion<T>[],
  fromIndex: number,
  toIndex: number,
): { object: T; isMigrated: boolean } {
  let converted = simpleDeepCopy(object) as T;
  let isMigrated = false;

  if (toIndex > fromIndex) {
    for (let i = fromIndex + 1; i <= toIndex; i++) {
      const conversion = conversions[i];
      if (conversion.shouldMigrate && !conversion.shouldMigrate(converted, true)) break;
      converted = applyRules(converted, conversion, true);
      converted.version = conversion.version;
      isMigrated = true;
    }
  } else {
    for (let i = fromIndex; i > toIndex; i--) {
      const conversion = conversions[i];
      if (conversion.shouldMigrate && !conversion.shouldMigrate(converted, false)) break;
      converted = applyRules(converted, conversion, false);
      const prevVersion = conversions[i - 1]?.version ?? null;
      if (prevVersion === null) delete converted.version;
      else converted.version = prevVersion;
      isMigrated = true;
    }
  }

  return { object: converted, isMigrated };
}

function applyRules<T extends VersionedObject>(
  object: T,
  conversion: Conversion<T>,
  isMigratingUp: boolean,
): T {
  return conversion.rules.reduce((obj, rule) => {
    if (!rule.isSuitable(obj, isMigratingUp)) return obj;
    const fn = isMigratingUp ? rule.migrateUp : rule.migrateDown;
    return fn?.(obj) ?? obj;
  }, object);
}
